import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { fetchTopics } from '../services/api';
import { useInstitution } from '../components/InstitutionContext';
import ThreadCard from '../components/ThreadCard';
import ThreadSearchBar from '../components/ThreadSearchBar';
import ThreadFilterTabs from '../components/ThreadFilterTabs';
import TopicSubtopicsSidebar from '../components/TopicSubtopicsSidebar';
import InfiniteScroll from 'react-infinite-scroll-component';
import FilledButton from '../components/FilledButton';
import TopicsSidebar from '../components/TopicsSidebar';
import TopContributorSidebar from '../components/TopContributorSidebar';
import RightSidebarContainer from '../components/RightSidebarContainer';
import CollapsibleSidebarSection from '../components/CollapsibleSidebarSection';
import LoginPromptAlert from '../components/LoginPromptAlert';
import VerifyEmailPromptAlert from '../components/VerifyEmailPromptAlert';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TopicIcon from '@mui/icons-material/Topic';
import { useAuth } from '../components/AuthContext';
import getUserDisplayName from '../utils/getUserDisplayName';
import AddPostDialog from '../components/AddPostDialog';


const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export default function TopicThreadsPage() {
  const { topicName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = location.state?.from || '/community';
  const { user, emailVerified } = useAuth();
  const verifiedUser = user && emailVerified;
  const unverifiedUser = user && !emailVerified;
  const { selectedInstitution } = useInstitution();
  const [threads, setThreads] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [subtopics, setSubtopics] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState(() => {
    const params = new URLSearchParams(location.search);
    const subs = params.get('subs');
    return subs ? subs.split(',').filter(Boolean).map((id) => Number(id)) : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('recent');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(10);
  const theme = useTheme();
  const [postOpen, setPostOpen] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const loadThreads = async () => {
      if (!selectedInstitution) {
        setTopics([]);
        setThreads([]);
        setSubtopics([]);
        return;
      }
      setLoadingData(true);
      try {
        const data = await fetchTopics(selectedInstitution);
        const topicStats = data.map(t => ({
          ...t,
          totalThreads: t.threads ? t.threads.length : 0,
        }));
        setTopics(topicStats);
        const filterName = decodeURIComponent(topicName || '').toLowerCase();

        const topic = data.find(
          (t) => (t.name || '').toLowerCase() === filterName
        );

        if (!topic) {
          setThreads([]);
          setSubtopics([]);
          return;
        }

        const allThreads = (topic.threads || []).map((thread) => ({
          ...thread,
          topicId: topic.id,
          topicName: topic.name,
          topicColor: topic.color || '#9c27b0',
          subtopicId: thread.subtopic_id || '',
          subtopicName: thread.subtopic || '',
        }));
        allThreads.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setThreads(allThreads);

        const subs = (topic.subtopics || [])
          .map((s) => ({ id: s.id, name: s.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setSubtopics(subs);
      } catch (err) {
        console.error('Failed to fetch topics:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadThreads();
  }, [topicName, selectedInstitution]);

  const fetchMoreThreads = () => {
    setTimeout(() => {
      setVisibleCount((prev) => prev + 10);
    }, 500);
  };

  // Update the URL when selected subtopics change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedSubtopics.length > 0) {
      params.set('subs', selectedSubtopics.join(','));
    } else {
      params.delete('subs');
    }
    const newSearch = params.toString();
    if (newSearch !== location.search.replace(/^\?/, '')) {
      navigate(
        { pathname: location.pathname, search: newSearch },
        { replace: true, state: location.state }
      );
    }
  // location.search is intentionally excluded to prevent rapid re-navigation
  // loops when selecting subtopics.
  }, [selectedSubtopics, navigate, location.pathname]);

  // Update selected subtopics when the URL changes (e.g., browser navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subs = params.get('subs');
    const ids = subs ? subs.split(',').filter(Boolean).map((id) => Number(id)) : [];
    setSelectedSubtopics((prev) => {
      const prevStr = prev.join(',');
      const newStr = ids.join(',');
      if (prevStr === newStr) return prev;
      return ids;
    });
  }, [location.search]);

  let filteredThreads = threads;
  if (searchQuery.trim() !== '') {
    const q = searchQuery.toLowerCase();
    filteredThreads = filteredThreads.filter(
      (thread) =>
        thread.title.toLowerCase().includes(q) ||
        stripHtml(thread.content).toLowerCase().includes(q)
    );
  }

  let tabFilteredThreads = [...filteredThreads];
  if (activeTab === 'unanswered') {
    tabFilteredThreads = tabFilteredThreads.filter((t) => (t.repliesCount || 0) === 0);
  } else if (activeTab === 'pinned') {
    tabFilteredThreads = tabFilteredThreads.filter((t) => t.is_pinned);
  } else if (activeTab === 'solved') {
    tabFilteredThreads = tabFilteredThreads.filter((t) => t.is_solved);
  } else if (activeTab === 'unsolved') {
    tabFilteredThreads = tabFilteredThreads.filter((t) => !t.is_solved);
  }

  let subtopicFilteredThreads = [...tabFilteredThreads];
  if (selectedSubtopics.length > 0) {
    const subNames = subtopics
      .filter((s) => selectedSubtopics.includes(s.id))
      .map((s) => s.name);
    subtopicFilteredThreads = subtopicFilteredThreads.filter((t) =>
      subNames.includes(t.subtopicName || '')
    );
  }

  if (sortBy === 'newest') {
    subtopicFilteredThreads.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } else if (sortBy === 'oldest') {
    subtopicFilteredThreads.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (sortBy === 'most_viewed') {
    subtopicFilteredThreads.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
  } else if (sortBy === 'most_commented') {
    subtopicFilteredThreads.sort((a, b) => (b.repliesCount || 0) - (a.repliesCount || 0));
  }

  const hasMore = visibleCount < subtopicFilteredThreads.length;

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        width: '100%',
        mx: 'auto',
        py: 2,
        minHeight: '80vh',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 4, py: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon color="success" />}
            onClick={() => navigate(previousPath)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>

          <Typography variant="h4" gutterBottom>
            Threads in {decodeURIComponent(topicName)}
          </Typography>

          {isSmallScreen && (
            <>
              <CollapsibleSidebarSection
                title={`${decodeURIComponent(topicName)} Subtopics`}
                icon={<TopicIcon fontSize="small" color="success" />}
              >
                <TopicSubtopicsSidebar
                  subtopics={subtopics}
                  selected={selectedSubtopics}
                  onSelect={setSelectedSubtopics}
                  topicName={decodeURIComponent(topicName)}
                />
              </CollapsibleSidebarSection>
              <CollapsibleSidebarSection
                title="Topics"
                icon={<LocalOfferIcon fontSize="small" color="success" />}
              >
                <TopicsSidebar
                  topics={topics}
                  selectedTopicName={decodeURIComponent(topicName)}
                  institutionId={selectedInstitution}
                />
              </CollapsibleSidebarSection>
              {verifiedUser && (
                <Button
                  component={RouterLink}
                  to={`/community/user/${encodeURIComponent(
                    getUserDisplayName(user)
                  )}`}
                  variant="outlined"
                  size="large"
                  sx={{ mb: 1 }}
                >
                  View My Posts
                </Button>
              )}
              {verifiedUser ? (
                <FilledButton
                  onClick={() => setPostOpen(true)}
                  text="Add a Post"
                  color="success"
                  size="large"
                  sx={{ mb: 2 }}
                  data-tour-id="community-add-post"
                />
              ) : !user ? (
                <LoginPromptAlert
                  from={location.pathname}
                  message="to Add a Post."
                  sx={{ mb: 2 }}
                />
              ) : (
                <VerifyEmailPromptAlert
                  message="to Add a Post."
                  sx={{ mb: 2 }}
                />
              )}
            </>
          )}

          {isSmallScreen ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  mb: 2,
                }}
              >
                <ThreadSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="most_viewed">Most Viewed</MenuItem>
                    <MenuItem value="most_commented">Most Commented</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 2 }}>
                <ThreadFilterTabs value={activeTab} onChange={setActiveTab} />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                mb: 3,
                gap: 2,
              }}
            >
              <ThreadFilterTabs value={activeTab} onChange={setActiveTab} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                <ThreadSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="most_viewed">Most Viewed</MenuItem>
                    <MenuItem value="most_commented">Most Commented</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          <InfiniteScroll
            dataLength={Math.min(visibleCount, subtopicFilteredThreads.length)}
            next={fetchMoreThreads}
            hasMore={hasMore}
            loader={<Typography variant="body2" sx={{ my: 2 }}>Loading...</Typography>}
          >
            {subtopicFilteredThreads.slice(0, visibleCount).map((thread) => (
              <ThreadCard key={thread.id} thread={thread} disableVoting={loadingData} />
            ))}
          </InfiniteScroll>
        </Box>

        {!isSmallScreen && (
          <RightSidebarContainer sx={{ ml: 3, mt: 6 }}>
            {verifiedUser && (
              <Button
                component={RouterLink}
                to={`/community/user/${encodeURIComponent(
                  getUserDisplayName(user)
                )}`}
                variant="outlined"
                size="large"
                sx={{ width: '100%', mb: 1 }}
              >
                View My Posts
              </Button>
            )}
            {verifiedUser ? (
              <FilledButton
                onClick={() => setPostOpen(true)}
                text="Add a Post"
                color="success"
                size="large"
                sx={{ width: '100%', mb: 2 }}
                data-tour-id="community-add-post"
              />
            ) : !user ? (
              <LoginPromptAlert
                from={location.pathname}
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            ) : (
              <VerifyEmailPromptAlert
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            )}
            <TopicSubtopicsSidebar
              subtopics={subtopics}
              selected={selectedSubtopics}
              onSelect={setSelectedSubtopics}
              topicName={decodeURIComponent(topicName)}
            />
            <TopicsSidebar
              topics={topics}
              selectedTopicName={decodeURIComponent(topicName)}
              institutionId={selectedInstitution}
            />
            <TopContributorSidebar contributors={[]} />
      </RightSidebarContainer>
        )}
      </Box>
      <AddPostDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        previousPath={location.pathname}
      />
    </Box>
  );
}
